import React, { createContext, useContext, useReducer, useCallback } from 'react';

// Initial state
const initialState = {
    loading: false,
    error: null,
    notifications: [],
    theme: 'light',
    user: null,
};

// Action types
const ACTIONS = {
    SET_LOADING: 'SET_LOADING',
    SET_ERROR: 'SET_ERROR',
    CLEAR_ERROR: 'CLEAR_ERROR',
    ADD_NOTIFICATION: 'ADD_NOTIFICATION',
    REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
    SET_THEME: 'SET_THEME',
    SET_USER: 'SET_USER',
};

// Reducer
const appReducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_LOADING:
            return { ...state, loading: action.payload };
        
        case ACTIONS.SET_ERROR:
            return { ...state, error: action.payload };
        
        case ACTIONS.CLEAR_ERROR:
            return { ...state, error: null };
        
        case ACTIONS.ADD_NOTIFICATION:
            return {
                ...state,
                notifications: [...state.notifications, action.payload],
            };
        
        case ACTIONS.REMOVE_NOTIFICATION:
            return {
                ...state,
                notifications: state.notifications.filter(
                    notification => notification.id !== action.payload,
                ),
            };
        
        case ACTIONS.SET_THEME:
            return { ...state, theme: action.payload };
        
        case ACTIONS.SET_USER:
            return { ...state, user: action.payload };
        
        default:
            return state;
    }
};

// Create context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
    const [state, dispatch] = useReducer(appReducer, initialState);

    // Actions
    const setLoading = useCallback((loading) => {
        dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
    }, []);

    const setError = useCallback((error) => {
        dispatch({ type: ACTIONS.SET_ERROR, payload: error });
    }, []);

    const clearError = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ERROR });
    }, []);

    const addNotification = useCallback((notification) => {
        const id = Date.now();
        dispatch({
            type: ACTIONS.ADD_NOTIFICATION,
            payload: { ...notification, id },
        });
    }, []);

    const removeNotification = useCallback((id) => {
        dispatch({ type: ACTIONS.REMOVE_NOTIFICATION, payload: id });
    }, []);

    const setTheme = useCallback((theme) => {
        dispatch({ type: ACTIONS.SET_THEME, payload: theme });
    }, []);

    const setUser = useCallback((user) => {
        dispatch({ type: ACTIONS.SET_USER, payload: user });
    }, []);

    const value = {
        ...state,
        setLoading,
        setError,
        clearError,
        addNotification,
        removeNotification,
        setTheme,
        setUser,
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};

// Custom hook to use the context
export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppContext must be used within an AppProvider');
    }
    return context;
}; 